const TABLE_COLUMN_WIDTH_KEY = 'table-column-widths';

// Default column widths (pixels) set by admin - applied to new users on first visit.
// Keys are the stable table id attributes set in each component's TSX.
const DEFAULT_COLUMN_WIDTHS: Record<string, Record<number, number>> = {
  'my-submissions-table': {0:56,1:234,2:192,3:209,4:104,5:113,6:114,7:114,8:108,9:172,10:154},
  'browse-initiatives-table': {0:192,1:191,2:309,3:190,4:187,5:186,6:101,7:100,8:183,9:26},
  'manage-divisions-table': {0:91,1:225,2:225},
  'dq-pending-table': {0:183,1:183,2:183,3:183,4:183,5:183,6:183,7:183,8:183},
  'manage-delegates-table': {0:188,1:188,2:188,3:188,4:188,5:192},
  'manage-dq-reviewers-table': {0:188,1:188,2:188,3:188,4:188,5:192},
  'partnership-sent-table': {0:288,1:288,2:403,3:255,4:182,5:289},
};

export const initResizableTables = () => {
  // Select ALL tables on the page that have th headers
  const tables = Array.from(document.querySelectorAll<HTMLTableElement>('table'));
  const tablesWithHeaders = tables.filter(t => t.querySelector('th'));

  tablesWithHeaders.forEach((table, tableIndex) => {
    if (table.getAttribute('data-resizable-initialized')) {
      return;
    }

    // Assign stable ID based on position in DOM
    const tableId = table.id || `resizable-table-${tableIndex}`;
    if (!table.id) table.id = tableId;

    table.setAttribute('data-resizable-initialized', 'true');

    // Ensure table layout is fixed so column widths are respected
    table.style.tableLayout = 'fixed';
    table.style.width = '100%';

    const headers = Array.from(table.querySelectorAll<HTMLElement>('th'));
    const savedWidths = getSavedColumnWidths(tableId);
    const savedCount = Object.keys(savedWidths).length;

    // If saved column count doesn't match current (e.g. column added/removed), discard saved widths
    const effectiveSaved = savedCount === headers.length ? savedWidths : {};

    if (Object.keys(effectiveSaved).length > 0) {
      // Apply user's saved widths
      headers.forEach((header, colIndex) => {
        if (effectiveSaved[colIndex]) {
          header.style.width = `${effectiveSaved[colIndex]}px`;
        }
      });
    } else if (DEFAULT_COLUMN_WIDTHS[tableId] && Object.keys(DEFAULT_COLUMN_WIDTHS[tableId]).length === headers.length) {
      // Apply hardcoded admin defaults
      const defaults = DEFAULT_COLUMN_WIDTHS[tableId];
      headers.forEach((header, colIndex) => {
        if (defaults[colIndex]) {
          header.style.width = `${defaults[colIndex]}px`;
        }
      });
      saveColumnWidths(tableId, defaults);
    } else {
      // Distribute evenly across all columns on first load
      requestAnimationFrame(() => {
        const tableWidth = table.offsetWidth;
        const equalWidth = Math.floor(tableWidth / headers.length);
        const initialWidths: Record<number, number> = {};
        headers.forEach((header, colIndex) => {
          const w = colIndex === headers.length - 1
            ? tableWidth - equalWidth * (headers.length - 1)  // last column gets remainder
            : equalWidth;
          header.style.width = `${w}px`;
          initialWidths[colIndex] = w;
        });
        saveColumnWidths(tableId, initialWidths);
      });
    }

    // Add resize handles to each header
    headers.forEach((header, colIndex) => {
      if (header.querySelector('.column-resizer')) return;

      header.style.position = 'relative';
      header.style.overflow = 'visible';

      const resizer = document.createElement('div');
      resizer.className = 'column-resizer';
      resizer.style.cssText = [
        'position:absolute', 'right:0', 'top:0', 'bottom:0',
        'width:6px', 'cursor:col-resize', 'background-color:transparent',
        'user-select:none', 'z-index:100', 'pointer-events:auto'
      ].join(';');
      // Thin visual line centred in the hit area
      const bar = document.createElement('div');
      bar.style.cssText = 'position:absolute;top:0;bottom:0;right:2px;width:2px;background-color:#d0d0d0;pointer-events:none;';
      resizer.appendChild(bar);

      header.appendChild(resizer);
      resizer.addEventListener('mousedown', (e) => startResize(e, header, table, tableId, colIndex));
    });
  });
};

const startResize = (
  e: MouseEvent,
  header: HTMLElement,
  table: HTMLTableElement,
  tableId: string,
  colIndex: number
) => {
  e.preventDefault();
  e.stopPropagation();

  const allHeaders = Array.from(table.querySelectorAll<HTMLElement>('th'));
  const isLastColumn = colIndex === allHeaders.length - 1;

  if (isLastColumn) {
    // For the last column: lock all columns (including last) and allow the table
    // to grow beyond 100% so the last column can actually resize.
    allHeaders.forEach(h => { h.style.width = `${h.offsetWidth}px`; });
    table.style.width = 'auto';
    table.style.minWidth = '100%';
  } else {
    // For non-last columns: lock all columns EXCEPT the last.
    // The last column flexibly absorbs the width change, preventing overflow.
    allHeaders.forEach((h, i) => {
      if (i < allHeaders.length - 1) {
        h.style.width = `${h.offsetWidth}px`;
      }
    });
  }

  const startX = e.clientX;
  const startWidth = header.offsetWidth;

  document.body.style.cursor = 'col-resize';
  document.body.style.userSelect = 'none';
  table.style.pointerEvents = 'none';

  const handleMouseMove = (mv: MouseEvent) => {
    const newWidth = Math.max(50, startWidth + (mv.clientX - startX));
    header.style.width = `${newWidth}px`;
  };

  const handleMouseUp = () => {
    document.body.style.cursor = 'auto';
    document.body.style.userSelect = 'auto';
    table.style.pointerEvents = 'auto';

    if (isLastColumn) {
      // Restore table to fill container (column widths are already saved as pixels)
      table.style.width = '100%';
      table.style.minWidth = '';
    }

    // Save all current widths
    const widths: Record<number, number> = {};
    allHeaders.forEach((h, i) => { widths[i] = h.offsetWidth; });
    saveColumnWidths(tableId, widths);

    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  };

  document.addEventListener('mousemove', handleMouseMove);
  document.addEventListener('mouseup', handleMouseUp);
};

const getSavedColumnWidths = (tableId: string): Record<number, number> => {
  try {
    const data = localStorage.getItem(TABLE_COLUMN_WIDTH_KEY);
    const allWidths = data ? JSON.parse(data) : {};
    return allWidths[tableId] || {};
  } catch {
    return {};
  }
};

const getTableColumnWidths = (_tableId: string): Record<number, number> => {
  return {};
};

const saveColumnWidths = (tableId: string, widths: Record<number, number>) => {
  try {
    const data = localStorage.getItem(TABLE_COLUMN_WIDTH_KEY);
    const allWidths = data ? JSON.parse(data) : {};
    allWidths[tableId] = widths;
    localStorage.setItem(TABLE_COLUMN_WIDTH_KEY, JSON.stringify(allWidths));
  } catch {
    console.warn('Could not save table column widths to localStorage');
  }
};

export const resetTableColumnWidths = (tableId?: string) => {
  try {
    if (tableId) {
      const data = localStorage.getItem(TABLE_COLUMN_WIDTH_KEY);
      const allWidths = data ? JSON.parse(data) : {};
      delete allWidths[tableId];
      localStorage.setItem(TABLE_COLUMN_WIDTH_KEY, JSON.stringify(allWidths));
    } else {
      localStorage.removeItem(TABLE_COLUMN_WIDTH_KEY);
    }
  } catch {
    console.warn('Could not reset table column widths');
  }
};
